import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { LanguageProvider, useLang } from "@/context/LanguageContext";
import { AppShell } from "@/components/AppShell";
import Login, { defaultRouteFor } from "@/pages/Login";
import TodayRoute from "@/pages/inspector/Route";
import LoomDetail from "@/pages/inspector/LoomDetail";
import InspectionForm from "@/pages/inspector/InspectionForm";
import MyEscalations from "@/pages/inspector/Escalations";
import Scorecard from "@/pages/inspector/Scorecard";
import AssignmentQueue from "@/pages/supervisor/AssignmentQueue";
import LiveBoard from "@/pages/supervisor/LiveBoard";
import EscalationsInbox from "@/pages/supervisor/EscalationsInbox";
import RoutePlanner from "@/pages/supervisor/RoutePlanner";
import GradeReview from "@/pages/supervisor/GradeReview";
import MissingEtas from "@/pages/supervisor/MissingEtas";
import Unallocated from "@/pages/supervisor/Unallocated";
import PORegister from "@/pages/coordinator/PORegister";
import WeeklyMIS from "@/pages/coordinator/WeeklyMIS";
import MasterData from "@/pages/coordinator/MasterData";
import NewPO from "@/pages/merchandiser/NewPO";
import MyPOs from "@/pages/merchandiser/MyPOs";
import DirectorDashboard from "@/pages/director/Dashboard";
import ImportData from "@/pages/director/ImportData";
import AuditMode from "@/pages/supervisor/AuditMode";
import AuditMismatches from "@/pages/supervisor/AuditMismatches";
import Notifications from "@/pages/Notifications";
import PODetail from "@/pages/PODetail";
import Landing from "@/pages/Landing";
import WeaverProfile from "@/pages/WeaverProfile";
import ImportHealth from "@/pages/ImportHealth";
import DeviceSyncStatus from "@/pages/supervisor/DeviceSyncStatus";
import ReworkSlip from "@/pages/ReworkSlip";
import Help from "@/pages/Help";
// Phase 2 factory
import FactoryWIP from "@/pages/factory/FactoryWIP";
import GRNEntry from "@/pages/factory/GRNEntry";
import PieceDetail from "@/pages/factory/PieceDetail";
import FinishingQueue from "@/pages/factory/FinishingQueue";
import StockOnHand from "@/pages/factory/StockOnHand";
import DespatchBuilder from "@/pages/factory/DespatchBuilder";
import VendorFollowup from "@/pages/factory/VendorFollowup";
import QRLabelSheet from "@/pages/factory/QRLabelSheet";
// Phase 3 portal + MG workspace
import VendorPortal from "@/pages/portal/VendorPortal";
import PortalTokens from "@/pages/director/PortalTokens";
import MGDashboard from "@/pages/mg/MGDashboard";
import MGInspections from "@/pages/mg/MGInspections";
import MGYarnLedger from "@/pages/mg/MGYarnLedger";
import MGPieces from "@/pages/mg/MGPieces";
// Phase 4 — RIC godown
import RICDashboard from "@/pages/ric/RICDashboard";
import RICReceive from "@/pages/ric/RICReceive";
import RICTransfer from "@/pages/ric/RICTransfer";
import RICWeaverSale from "@/pages/ric/RICWeaverSale";
import RICPOQueue from "@/pages/ric/RICPOQueue";
import RICStock from "@/pages/ric/RICStock";
import RICCount from "@/pages/ric/RICCount";
import RICDiscrepancies from "@/pages/ric/RICDiscrepancies";
import MGYarnPO from "@/pages/mg/MGYarnPO";
import RICTradeLog from "@/pages/director/RICTradeLog";
// Phase 5 — Job Work · Gate Pass · HR-Lite
import JobWorkRegister from "@/pages/jobwork/JobWorkRegister";
import NewJobWorkChallan from "@/pages/jobwork/NewJobWorkChallan";
import JobWorkChallanDetail from "@/pages/jobwork/JobWorkChallanDetail";
import JobWorkerMaster from "@/pages/jobwork/JobWorkerMaster";
import GuardToday from "@/pages/gate/GuardToday";
import GatePassRegister from "@/pages/gate/GatePassRegister";
import GatePassReceipt from "@/pages/gate/GatePassReceipt";
import VisitorLog from "@/pages/gate/VisitorLog";
import VisitorEOD from "@/pages/gate/VisitorEOD";
import StaffMaster from "@/pages/hr/StaffMaster";
import DailyAttendance from "@/pages/hr/DailyAttendance";
import MonthGrid from "@/pages/hr/MonthGrid";
import LeaveRequests from "@/pages/hr/LeaveRequests";
import Maintenance from "@/pages/admin/Maintenance";
import CSVUpload from "@/pages/admin/CSVUpload";
import KRALeaderboard from "@/pages/admin/KRALeaderboard";
import RefreshChecklist from "@/pages/admin/RefreshChecklist";

function LangDefaults({ children }) {
  const { user } = useAuth();
  const { lang, setLang } = useLang();
  React.useEffect(() => {
    const stored = localStorage.getItem("hag_lang");
    if (stored) return;
    if (user?.role === "inspector") setLang("hi");
    else if (user) setLang("en");
  }, [user]); // eslint-disable-line
  return children;
}

function RequireAuth({ children, roles }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="p-6 text-sm text-[#7C8E9E]">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to={defaultRouteFor(user.role)} replace />;
  return <AppShell>{children}</AppShell>;
}

function AppRoutes() {
  const { user } = useAuth();
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/" element={user ? <Navigate to={defaultRouteFor(user.role)} replace /> : <Landing />} />
      <Route path="/landing" element={<Landing />} />

      <Route path="/inspector/route" element={<RequireAuth roles={["inspector"]}><TodayRoute /></RequireAuth>} />
      <Route path="/inspector/loom/:loomId" element={<RequireAuth roles={["inspector", "supervisor", "director"]}><LoomDetail /></RequireAuth>} />
      <Route path="/inspector/loom/:loomId/inspect" element={<RequireAuth roles={["inspector", "supervisor"]}><InspectionForm /></RequireAuth>} />
      <Route path="/inspector/escalations" element={<RequireAuth roles={["inspector"]}><MyEscalations /></RequireAuth>} />
      <Route path="/inspector/scorecard" element={<RequireAuth roles={["inspector"]}><Scorecard /></RequireAuth>} />

      <Route path="/supervisor/assignment-queue" element={<RequireAuth roles={["supervisor", "director"]}><AssignmentQueue /></RequireAuth>} />
      <Route path="/supervisor/live-board" element={<RequireAuth roles={["supervisor", "director", "coordinator"]}><LiveBoard /></RequireAuth>} />
      <Route path="/supervisor/escalations" element={<RequireAuth roles={["supervisor", "director"]}><EscalationsInbox /></RequireAuth>} />
      <Route path="/supervisor/route-planner" element={<RequireAuth roles={["supervisor", "director"]}><RoutePlanner /></RequireAuth>} />
      <Route path="/supervisor/grade-review" element={<RequireAuth roles={["supervisor", "director"]}><GradeReview /></RequireAuth>} />
      <Route path="/supervisor/missing-etas" element={<RequireAuth roles={["supervisor", "director"]}><MissingEtas /></RequireAuth>} />
      <Route path="/supervisor/unallocated" element={<RequireAuth roles={["supervisor", "director", "coordinator"]}><Unallocated /></RequireAuth>} />
      <Route path="/supervisor/audit" element={<RequireAuth roles={["supervisor", "director"]}><AuditMode /></RequireAuth>} />
      <Route path="/supervisor/audit-mismatches" element={<RequireAuth roles={["supervisor", "director", "coordinator"]}><AuditMismatches /></RequireAuth>} />

      <Route path="/coordinator/po-register" element={<RequireAuth roles={["coordinator", "supervisor", "director"]}><PORegister /></RequireAuth>} />
      <Route path="/coordinator/weekly-mis" element={<RequireAuth roles={["coordinator", "director"]}><WeeklyMIS /></RequireAuth>} />
      <Route path="/coordinator/master-data" element={<RequireAuth roles={["coordinator", "director"]}><MasterData /></RequireAuth>} />

      <Route path="/merch/new-po" element={<RequireAuth roles={["merchandiser"]}><NewPO /></RequireAuth>} />
      <Route path="/merch/my-pos" element={<RequireAuth roles={["merchandiser", "director"]}><MyPOs /></RequireAuth>} />

      <Route path="/director/dashboard" element={<RequireAuth roles={["director"]}><DirectorDashboard /></RequireAuth>} />
      <Route path="/director/import" element={<RequireAuth roles={["director", "coordinator"]}><ImportData /></RequireAuth>} />
      <Route path="/admin/import-health" element={<RequireAuth roles={["director", "coordinator", "supervisor"]}><ImportHealth /></RequireAuth>} />
      <Route path="/supervisor/device-sync" element={<RequireAuth roles={["supervisor", "director"]}><DeviceSyncStatus /></RequireAuth>} />
      <Route path="/weaver/:weaverId" element={<RequireAuth roles={["supervisor", "coordinator", "director"]}><WeaverProfile /></RequireAuth>} />

      <Route path="/notifications" element={<RequireAuth><Notifications /></RequireAuth>} />
      <Route path="/help" element={<RequireAuth><Help /></RequireAuth>} />
      <Route path="/rework-slip/:escId" element={<RequireAuth roles={["supervisor", "coordinator", "director"]}><ReworkSlip /></RequireAuth>} />
      <Route path="/po/:poId" element={<RequireAuth><PODetail /></RequireAuth>} />

      {/* Phase 2 Factory */}
      <Route path="/factory/wip" element={<RequireAuth roles={["prod_qc_coordinator", "qc_officer", "stores_coordinator", "coordinator", "director"]}><FactoryWIP /></RequireAuth>} />
      <Route path="/factory/grn" element={<RequireAuth roles={["stores_coordinator", "prod_qc_coordinator", "director"]}><GRNEntry /></RequireAuth>} />
      <Route path="/factory/piece/:pieceId" element={<RequireAuth><PieceDetail /></RequireAuth>} />
      <Route path="/factory/finishing" element={<RequireAuth roles={["wash_master", "finisher", "prod_qc_coordinator", "coordinator", "director"]}><FinishingQueue /></RequireAuth>} />
      <Route path="/factory/stock" element={<RequireAuth roles={["prod_qc_coordinator", "stores_coordinator", "coordinator", "director"]}><StockOnHand /></RequireAuth>} />
      <Route path="/factory/despatch" element={<RequireAuth roles={["stores_coordinator", "prod_qc_coordinator", "director"]}><DespatchBuilder /></RequireAuth>} />
      <Route path="/factory/vendor-followup" element={<RequireAuth roles={["coordinator", "supervisor", "director"]}><VendorFollowup /></RequireAuth>} />
      <Route path="/factory/qr-labels/:grnId" element={<RequireAuth roles={["stores_coordinator", "prod_qc_coordinator", "director"]}><QRLabelSheet /></RequireAuth>} />
      <Route path="/factory/qr-labels" element={<RequireAuth roles={["stores_coordinator", "prod_qc_coordinator", "director", "supervisor", "coordinator", "qc_officer"]}><QRLabelSheet /></RequireAuth>} />

      {/* Phase 3 — Public vendor magic-link portal (no shell, no auth) */}
      <Route path="/portal/vendor" element={<VendorPortal />} />

      {/* Phase 3 — Director-admin: manage magic-link tokens */}
      <Route path="/director/portal-tokens" element={<RequireAuth roles={["director"]}><PortalTokens /></RequireAuth>} />

      {/* Phase 3 — MG Rugs workspace (login required) */}
      <Route path="/mg/dashboard" element={<RequireAuth roles={["vendor_mg"]}><MGDashboard /></RequireAuth>} />
      <Route path="/mg/inspections" element={<RequireAuth roles={["vendor_mg", "director", "supervisor", "coordinator", "prod_qc_coordinator"]}><MGInspections /></RequireAuth>} />
      <Route path="/mg/yarn" element={<RequireAuth roles={["vendor_mg"]}><MGYarnLedger /></RequireAuth>} />
      <Route path="/mg/pieces" element={<RequireAuth roles={["vendor_mg"]}><MGPieces /></RequireAuth>} />

      {/* Phase 4 — RIC godown (staff + supervisor) */}
      <Route path="/ric/dashboard" element={<RequireAuth roles={["ric_staff", "ric_supervisor"]}><RICDashboard /></RequireAuth>} />
      <Route path="/ric/receive" element={<RequireAuth roles={["ric_staff", "ric_supervisor"]}><RICReceive /></RequireAuth>} />
      <Route path="/ric/transfer" element={<RequireAuth roles={["ric_staff", "ric_supervisor"]}><RICTransfer /></RequireAuth>} />
      <Route path="/ric/weaver-sale" element={<RequireAuth roles={["ric_staff", "ric_supervisor"]}><RICWeaverSale /></RequireAuth>} />
      <Route path="/ric/po-queue" element={<RequireAuth roles={["ric_staff", "ric_supervisor"]}><RICPOQueue /></RequireAuth>} />
      <Route path="/ric/stock" element={<RequireAuth roles={["ric_staff", "ric_supervisor", "director", "coordinator", "vendor_mg"]}><RICStock /></RequireAuth>} />
      <Route path="/ric/count" element={<RequireAuth roles={["ric_staff", "ric_supervisor"]}><RICCount /></RequireAuth>} />
      <Route path="/ric/discrepancies" element={<RequireAuth roles={["ric_staff", "ric_supervisor", "vendor_mg", "director"]}><RICDiscrepancies /></RequireAuth>} />
      <Route path="/mg/yarn-po" element={<RequireAuth roles={["vendor_mg"]}><MGYarnPO /></RequireAuth>} />
      <Route path="/director/ric-trade" element={<RequireAuth roles={["director", "coordinator"]}><RICTradeLog /></RequireAuth>} />

      {/* Phase 5 — Job Work Challan */}
      <Route path="/coordinator/job-work" element={<RequireAuth roles={["coordinator", "director", "stores_coordinator", "prod_qc_coordinator", "supervisor"]}><JobWorkRegister /></RequireAuth>} />
      <Route path="/coordinator/job-work/new" element={<RequireAuth roles={["coordinator", "director", "stores_coordinator", "prod_qc_coordinator", "supervisor"]}><NewJobWorkChallan /></RequireAuth>} />
      <Route path="/coordinator/job-work/:cid" element={<RequireAuth roles={["coordinator", "director", "stores_coordinator", "prod_qc_coordinator", "supervisor"]}><JobWorkChallanDetail /></RequireAuth>} />
      <Route path="/coordinator/job-workers" element={<RequireAuth roles={["coordinator", "director", "supervisor"]}><JobWorkerMaster /></RequireAuth>} />

      {/* Phase 5 — Gate Passes */}
      <Route path="/guard/today" element={<RequireAuth roles={["security_guard"]}><GuardToday /></RequireAuth>} />
      <Route path="/gate/register" element={<RequireAuth roles={["security_guard", "supervisor", "coordinator", "director", "prod_qc_coordinator", "stores_coordinator"]}><GatePassRegister /></RequireAuth>} />
      <Route path="/gate/receipt/:gpId" element={<RequireAuth roles={["security_guard", "supervisor", "coordinator", "director", "prod_qc_coordinator", "stores_coordinator"]}><GatePassReceipt /></RequireAuth>} />
      <Route path="/gate/visitors" element={<RequireAuth roles={["security_guard", "supervisor", "coordinator", "director"]}><VisitorLog /></RequireAuth>} />
      <Route path="/gate/visitors/eod" element={<RequireAuth roles={["security_guard", "supervisor", "coordinator", "director"]}><VisitorEOD /></RequireAuth>} />

      {/* Phase 5 — HR-Lite */}
      <Route path="/hr/staff" element={<RequireAuth roles={["director", "coordinator", "supervisor", "prod_qc_coordinator"]}><StaffMaster /></RequireAuth>} />
      <Route path="/hr/attendance" element={<RequireAuth roles={["director", "coordinator", "supervisor", "prod_qc_coordinator"]}><DailyAttendance /></RequireAuth>} />
      <Route path="/hr/attendance/month" element={<RequireAuth roles={["director", "coordinator", "supervisor", "prod_qc_coordinator"]}><MonthGrid /></RequireAuth>} />
      <Route path="/hr/leaves" element={<RequireAuth><LeaveRequests /></RequireAuth>} />

      {/* Phase 6 — Admin Maintenance, CSV, KRA */}
      <Route path="/admin/maintenance" element={<RequireAuth roles={["director"]}><Maintenance /></RequireAuth>} />
      <Route path="/admin/csv-upload" element={<RequireAuth roles={["director", "coordinator", "supervisor", "prod_qc_coordinator", "stores_coordinator", "merchandiser"]}><CSVUpload /></RequireAuth>} />
      <Route path="/admin/kra-leaderboard" element={<RequireAuth roles={["director"]}><KRALeaderboard /></RequireAuth>} />
      <Route path="/admin/refresh" element={<RequireAuth roles={["director", "coordinator"]}><RefreshChecklist /></RequireAuth>} />

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <LanguageProvider>
        <AuthProvider>
          <LangDefaults>
            <AppRoutes />
          </LangDefaults>
        </AuthProvider>
      </LanguageProvider>
    </BrowserRouter>
  );
}
